import { Component, Input, Output, EventEmitter, inject, ChangeDetectorRef, OnInit } from '@angular/core';
import { NgComponentOutlet } from '@angular/common';
import { JobHistoryDetailComponentLexusTableSchema, JobHistoryDetailComponentToyotaTableSchema, JobHistoryDetailComponentTTENTableSchema, JobHistoryDetailDbKeys, JobHistoryDetailSearchConfig, jobHistoryDetailTableSchema, JobHistoryExportFileName, tenureHistoryActionSearchConfig } from '../../model/job-history-detail.model';
import { DynamicDataTableComponent } from '../../layout/dynamic-data-table/dynamic-data-table.component';
import { JobHistroyDataSchema } from '../../model/job-history.model';
import { API_STATUS, ApiEnvNames, ERROR_RESPONSE, SearchConfig } from '../../model/api.model';
import { FieldNames } from '../../model/form-fields.model';
import { CommonService } from '../../services/common.service';
import { CommonUtils } from '../../utils/common.utils';
import { DownloadButtonComponent } from '../download-button/download-button.component';
import { TableColumnSchema } from '../../model/dynamic-data-table.model';
import { ExcelExportService } from '../../services/excel-export.service';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatDialog } from '@angular/material/dialog';
import { TenureHistoryComponent } from '../tenure/tenure-history/tenure-history.component';
import {
  LucideArrowLeft,
  LucideBuilding2,
  LucideUsers,
  LucideBriefcase,
  LucideList,
  LucideAward,
  LucideLayoutList
} from '@lucide/angular';

@Component({
  selector: 'job-history-detail',
  standalone: true,
  imports: [DynamicDataTableComponent, DownloadButtonComponent, MatExpansionModule, NgComponentOutlet],
  templateUrl: './job-history-detail.component.html',
  styleUrl: './job-history-detail.component.scss'
})
export class JobHistoryDetailComponent implements OnInit{

  @Input() dataSet: any;

  protected readonly icons = {
    arrowLeft: LucideArrowLeft,
    building: LucideBuilding2,
    users: LucideUsers,
    briefcase: LucideBriefcase,
    list: LucideList,
    award: LucideAward,
    layoutList: LucideLayoutList
  };
  @Output() dataEmitter = new EventEmitter<boolean>();
  
  public associateDetails: Array<jobHistoryDetailTableSchema> = [];
  public readonly JobHistoryDetailSearchConfig:SearchConfig = structuredClone(JobHistoryDetailSearchConfig);
  private readonly service = inject(CommonService);  
  changeRef = inject(ChangeDetectorRef);
  private readonly exportService = inject(ExcelExportService); 
  private readonly commonUtils = inject(CommonUtils);
  private readonly dialog = inject(MatDialog);
  public exportData:any[] = [];
  public exportDataSet:any = {}
  public exportMergeConfig: Record<string, string[]> = {};
  backToJobHistory() {
    this.commonUtils.spinner('show');
    this.dataEmitter.emit()
  }
  ngOnInit(){
    this.initializeDataSchemas()
  }
  initializeDataSchemas(){
    this.associateDetails = [];
    this.exportData = [];
    this.exportDataSet = {};
    this.exportMergeConfig = {};
    const associateType = this.dataSet[JobHistroyDataSchema['associateType']];
    const fetchPayload = {
      [FieldNames['SpinId']]: this.dataSet[JobHistroyDataSchema['spinId']],
      'associateType': ''
    }
    let schemasToAdd: any[] = [];
    if(associateType === 'LEXUS'){
      schemasToAdd = [structuredClone(JobHistoryDetailComponentLexusTableSchema)];
      fetchPayload['associateType'] = 'Lexus';
    }else if(associateType === 'TOYOTA'){
      schemasToAdd = [structuredClone(JobHistoryDetailComponentToyotaTableSchema), structuredClone(JobHistoryDetailComponentTTENTableSchema)];
      fetchPayload['associateType'] = 'Toyota';
    }else if(associateType === 'LEXUS/TOYOTA'){
      schemasToAdd = [structuredClone(JobHistoryDetailComponentLexusTableSchema), structuredClone(JobHistoryDetailComponentToyotaTableSchema), structuredClone(JobHistoryDetailComponentTTENTableSchema)];
      fetchPayload['associateType'] = 'Lexus/Toyota';
    }else{
      this.commonUtils.showMatSnackBar('Invalid Association Type');
      return;
    }
    this.associateDetails.push(...schemasToAdd);
    this.assignCallbacks(this.associateDetails);
    if(this.associateDetails.length > 0){
      this.initiateFetch(fetchPayload);
    }
  }
  assignCallbacks(associateDetails: Array<jobHistoryDetailTableSchema>) {
    for (const brandConfig of associateDetails) {
      const tableSchemaArray = brandConfig['tableSchemaArray'] ?? [];
      for (const associateConfig of tableSchemaArray) {
        this.assignTenureActionCallback(associateConfig['dataKey'], associateConfig?.schema?.columnSchema ?? []);
      }
    }
  }
  private assignTenureActionCallback(tableKey: string, columnSchema: TableColumnSchema[]): void { 
    for (const col of columnSchema) {
      if (col.fieldKey !== 'tenure_history_action' || !col.actionConfig) {
        continue;
      }
      const action = col['actionConfig'][0];
      action['onClick'] = (event: any) => this.showHistoryOnClick(event, tableKey);
    }
  }
  initiateFetch(fetchPayload:{spinId:string, associateType:string}){
    const postParam :SearchConfig = {
      ...structuredClone(this.JobHistoryDetailSearchConfig),
      body:fetchPayload
    }
    this.associateDetails.forEach(item => {
      item['tableSchemaArray']?.forEach(tableSchema => {
        if (tableSchema?.['schema']) {
          tableSchema['schema']['dataSource'] = [];
          tableSchema['schema']['activeSorts'] = [];
        }
      });
    });
    this.setNestedTablesLoadingState(true);
    
    this.service.getDataFromDataSource(postParam, ApiEnvNames['JobEnrollment']).then(res=>{
      if(res['status'] === API_STATUS['Success']){
        this.handleResponse(res['result'][0]);
        this.changeRef.detectChanges()
      }
      else {
         this.commonUtils.showMatSnackBar(ERROR_RESPONSE['errorFetch'])
      }
    }).catch(err=>{
      console.info(err);      
    }).finally(() => {
      this.setNestedTablesLoadingState(false);
      this.changeRef.detectChanges();
    })
  }
  private setNestedTablesLoadingState(isLoading: boolean): void {
    this.associateDetails.forEach(item => {
      if (!item['tableSchemaArray']) {
        return;
      }
      item['tableSchemaArray'].forEach(tableSchema => {
        if (tableSchema?.['schema']) {
          tableSchema['schema'].isLoading = isLoading;
        }
      });
    });
  }
  handleResponse(result:any){
    const dataSet = result;
    const exportDataSet:any = {};
    this.associateDetails.forEach(item=>{
      const orgName = item['headerLabel']
      if(item['tableSchemaArray']){
        item['tableSchemaArray'].forEach(tableSchema=>{
          tableSchema['schema'].activeSorts = [];
          let tableData = dataSet[tableSchema['dataKey']] ?? [];
          tableData = this.service.applyColumnMergesBySpinId(tableData, tableSchema['schema']?.columnSchema ?? []);
          tableSchema['schema']['dataSource'] = tableData
           const tableName = `${orgName} - ${tableSchema['title']}`
           const columnInfo:Array<TableColumnSchema> = tableSchema['schema']['columnSchema'];
           const mergeColumnLabels = columnInfo
            .filter(col => col.isColumnsMerge)
            .map(col => col.label);
           if (mergeColumnLabels.length > 0) {
            this.exportMergeConfig[tableName] = mergeColumnLabels;
           }
           if(columnInfo && tableData.length>0){
            tableData = tableData.map((record: any)=>{
              if(record[JobHistoryDetailDbKeys['EndDate']] === ''){
                record[JobHistoryDetailDbKeys['EndDate']] = "CURRENT"
              }
              if(record[JobHistoryDetailDbKeys['JobEndDate']] === ''){
                record[JobHistoryDetailDbKeys['JobEndDate']] = "CURRENT"
              }
              const transformed: any = {};
              for(const col of columnInfo){
                if(col['fieldKey'] !== 'tenure_history_action'){
                  transformed[col['label']] = record[col.fieldKey];
                }
              }
              return transformed;
            })
          }
          exportDataSet[tableName] = tableData;
  if(tableData.length>0){
            this.exportData = [...this.exportData, tableName]
          }
        })
      }
    })
    this.exportDataSet = exportDataSet
  }
  downloadBtnOnClick(_event:any){
    const data = this.exportDataSet;
    this.exportService.exportToMultipleSheets(data, JobHistoryExportFileName, this.exportMergeConfig)
  }
  showHistoryOnClick = (event: any, tableKey:string) => {
    const historySearchConfig = structuredClone(tenureHistoryActionSearchConfig[tableKey])
    event.tenureConfig = historySearchConfig;
    event[FieldNames['Brand']] = historySearchConfig['body'][FieldNames['Brand']]
    const dialogRef = this.dialog.open(TenureHistoryComponent, {
      panelClass: 'tenure-history-dialog',
      data: event
    });
    const instance = dialogRef.componentInstance;
    instance.btnClose.subscribe(() => {
      dialogRef.close()
    })
    instance.type = 'history';
  }
}
